import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Copy, KeyRound, MoreVertical, Pencil, Plus, Search, ShieldPlus, X } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import orgApi from '../api/organizations';
import useStore from '../store/useStore';
import NoOrgState from './NoOrgState';
import ConfirmModal from '../components/UI/ConfirmModal';
import InviteDrawer from '../components/OrgMembers/InviteDrawer';
import RoleDrawer from '../components/OrgMembers/RoleDrawer';
import OrgDetailsDrawer from '../components/OrgMembers/OrgDetailsDrawer';
import {
  monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, panelStyle,
  errMessage, initialsOf, formatDate, daysUntil,
} from '../components/UI/ink';

// One page for what used to be four tabs (Members, Role Manager,
// Organization, Workspaces). Old ?tab= links still work:
//   members → this page · roles → this page (Roles panel on the right)
//   organization → details drawer open · workspaces → sidebar org switcher
const STATUS_TABS = [['all', 'All'], ['active', 'Active'], ['pending', 'Invited']];

const RowMenu = ({ member, isSelf, can, onRemove, onResend, onRevoke }) => {
  // Menu position (fixed, portalled) so the table's scroll container can't clip it.
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const open = !!pos;

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setPos(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPos(null); };
    const onScroll = () => setPos(null);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const toggle = () => {
    if (open) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 120;
    setPos({ right: window.innerWidth - r.right, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };

  const pending = member.status !== 'active';
  const items = pending
    ? [
        can('users:create') && { label: 'Resend invitation', run: onResend },
        can('users:delete') && { label: 'Revoke invitation', run: onRevoke, danger: true },
      ]
    : [
        can('users:delete') && !isSelf && { label: 'Remove from organisation', run: onRemove, danger: true },
      ];
  const actions = items.filter(Boolean);
  if (actions.length === 0) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${member.full_name || member.email}`}
        className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-transparent hover:border-[color:var(--surf-line)]"
        style={{ color: 'var(--ink-faint)' }}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" className="fixed z-40 min-w-[200px] p-1 rounded-[10px] border text-left" style={{ ...pos, background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}>
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              onClick={() => { setPos(null); a.run(); }}
              className="w-full h-10 px-3 rounded-md text-left text-[13px] hover:bg-[color:var(--surf-raised)]"
              style={{ color: a.danger ? 'var(--sev-crit-fg)' : 'var(--ink-text)' }}
            >
              {a.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

const OrgMembers = () => {
  const { user, permissions: myPerms } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const setOrgSwitcherOpen = useStore(s => s.setOrgSwitcherOpen);
  const toggleSidebar = useStore(s => s.setSidebarMinimized);

  const tenantId = user?.tenant_id || localStorage.getItem('tenant_id') || user?.last_active_tenant_id;
  const can = useCallback(p => (myPerms || []).includes(p), [myPerms]);

  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [drawer, setDrawer] = useState(null); // 'invite' | 'org' | { role } | { role: null }
  const [confirm, setConfirm] = useState(null); // { type: 'remove' | 'revoke', member }
  const [copied, setCopied] = useState(false);

  const roleFilter = searchParams.get('role');
  const setRoleFilter = (name) => {
    const next = new URLSearchParams(searchParams);
    if (name) next.set('role', name); else next.delete('role');
    setSearchParams(next, { replace: true });
  };

  // Legacy ?tab= links and ?drawer= deep links (used by the sidebar switcher).
  useEffect(() => {
    const tab = searchParams.get('tab');
    const drawerParam = searchParams.get('drawer');
    if (!tab && !drawerParam) return;
    if (tab === 'organization' || drawerParam === 'org') setDrawer('org');
    else if (drawerParam === 'invite') setDrawer('invite');
    else if (tab === 'workspaces') {
      // Read live state: StrictMode runs this effect twice in dev.
      if (useStore.getState().sidebarMinimized) toggleSidebar();
      setOrgSwitcherOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.delete('drawer');
    setSearchParams(next, { replace: true });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!tenantId) return;
    const [m, r, p, o] = await Promise.all([
      orgApi.getMembers(tenantId).catch(() => null),
      orgApi.getRoles(tenantId).catch(() => null),
      orgApi.getPermissions(tenantId).catch(() => null),
      // Needs system:update; members without it just don't see the details.
      orgApi.getOrg(tenantId).catch(() => null),
    ]);
    if (m) setMembers(m.members || m.users || []);
    if (r) setRoles(Array.isArray(r) ? r : r.roles || []);
    if (p) setAllPerms(p.permissions || []);
    setOrg(o);
    if (!m) setNotice({ kind: 'error', text: 'Couldn’t load members.' });
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const flash = (kind, text) => setNotice({ kind, text });

  const changeRole = async (member, role) => {
    try {
      await orgApi.updateMemberRole(tenantId, member.id, role);
      flash('ok', `${member.full_name || member.email} is now ${role}.`);
      load();
    } catch (err) {
      flash('error', errMessage(err, 'Failed to update role.'));
    }
  };

  const resend = async (member) => {
    try {
      const res = await orgApi.resendInvitation(tenantId, member.id);
      if (res.email_sent) flash('ok', `Invitation resent to ${member.email}.`);
      else flash('warn', `New link for ${member.email}: ${res.invite_link || 'unavailable'}`);
      load();
    } catch (err) {
      flash('error', errMessage(err, 'Failed to resend invitation.'));
    }
  };

  const runConfirm = async () => {
    const { type, member } = confirm;
    setConfirm(null);
    try {
      if (type === 'remove') await orgApi.removeMember(tenantId, member.id);
      else await orgApi.revokeInvitation(tenantId, member.id);
      flash('ok', type === 'remove' ? `${member.email} removed.` : `Invitation for ${member.email} revoked.`);
      load();
    } catch (err) {
      flash('error', errMessage(err, 'Action failed.'));
    }
  };

  const myEmail = (user?.email || '').toLowerCase();
  const myRole = members.find(m => m.email?.toLowerCase() === myEmail)?.role || user?.role || user?.roles?.[0];

  const counts = useMemo(() => ({
    all: members.length,
    active: members.filter(m => m.status === 'active').length,
    pending: members.filter(m => m.status !== 'active').length,
  }), [members]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter(m =>
      (status === 'all' || (status === 'active' ? m.status === 'active' : m.status !== 'active'))
      && (!roleFilter || m.role === roleFilter)
      && (!q || m.email?.toLowerCase().includes(q) || (m.full_name || '').toLowerCase().includes(q)));
  }, [members, status, roleFilter, search]);

  const membersByRole = useMemo(() => {
    const map = {};
    members.forEach(m => { map[m.role] = (map[m.role] || 0) + 1; });
    return map;
  }, [members]);

  if (!tenantId) {
    return <div className="p-6"><NoOrgState /></div>;
  }

  const roleById = Object.fromEntries(roles.map(r => [r.id, r]));
  const canEditRoles = can('roles:update');
  const orgName = org?.name || `Org ${tenantId.substring(0, 8)}`;
  const editingRole = drawer && typeof drawer === 'object' ? drawer.role : undefined;

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-5" style={{ color: 'var(--ink-text)' }}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>ORGANISATION · ACCESS</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Members &amp; Roles</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>
            {loading ? 'Loading…' : `${counts.active} active member${counts.active === 1 ? '' : 's'} · ${counts.pending} pending invitation${counts.pending === 1 ? '' : 's'} · ${roles.length} role${roles.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          {can('roles:create') && (
            <button type="button" onClick={() => setDrawer({ role: null })} className={secondaryBtn} style={secondaryBtnStyle}>
              <ShieldPlus className="w-4 h-4" strokeWidth={1.8} />Create role
            </button>
          )}
          {can('users:create') && (
            <button type="button" onClick={() => setDrawer('invite')} className={primaryBtn} style={primaryBtnStyle}>
              <Plus className="w-4 h-4" strokeWidth={2} />Invite members
            </button>
          )}
        </div>
      </div>

      {notice && (
        <div
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className="flex items-start justify-between gap-3 px-4 py-3 rounded-[10px] border text-[13px] break-all"
          style={{
            borderColor: notice.kind === 'error' ? 'var(--sev-crit-fg)' : notice.kind === 'warn' ? 'var(--sev-med-fg)' : 'var(--sev-good-fg)',
            background: notice.kind === 'error' ? 'var(--sev-crit-bg)' : notice.kind === 'warn' ? 'var(--sev-med-bg)' : 'var(--sev-good-bg)',
          }}
        >
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="shrink-0" style={{ color: 'var(--ink-muted)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organisation identity strip (was the Organization tab) */}
      <section aria-label="Organisation" className="min-h-[76px] box-border flex flex-wrap items-center gap-4 px-5 py-3 rounded-[14px] border" style={panelStyle}>
        <span className="w-11 h-11 rounded-[10px] border flex items-center justify-center font-heading text-lg font-semibold shrink-0" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line)' }}>
          {orgName.charAt(0).toUpperCase()}
        </span>
        <div className="flex-grow flex flex-col gap-1 min-w-0">
          <span className="text-base font-semibold truncate">{orgName}</span>
          <span className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>
            <span className="flex items-center gap-1.5">
              Tenant <span className="font-mono" style={{ color: 'var(--ink-muted)' }}>{tenantId.substring(0, 8)}</span>
              <button
                type="button"
                aria-label={copied ? 'Tenant ID copied' : 'Copy tenant ID'}
                title={copied ? 'Copied' : 'Copy full tenant ID'}
                onClick={() => { navigator.clipboard.writeText(tenantId); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="w-7 h-7 flex items-center justify-center"
                style={{ color: copied ? 'var(--sev-good-fg)' : 'var(--ink-faint)' }}
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            </span>
            {org?.created_at && <span>Created {formatDate(org.created_at)}</span>}
            <span className="flex items-center gap-1.5">
              <span className="w-[7px] h-[7px] rounded-full" style={{ background: 'var(--sev-good-fg)' }} />Active
            </span>
          </span>
        </div>
        <button type="button" onClick={() => setDrawer('org')} className="h-10 px-3.5 rounded-[10px] border text-[13px] font-medium" style={secondaryBtnStyle}>
          {can('system:update') ? 'Edit details' : 'View details'}
        </button>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        {/* Members (was the Members tab) */}
        <section aria-label="Members" className="rounded-[14px] border overflow-hidden" style={panelStyle}>
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <div className="w-full sm:w-[260px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
              <Search className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
              <input
                type="search"
                aria-label="Search members"
                placeholder="Search name or email"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]"
                style={{ color: 'var(--ink-text)' }}
              />
            </div>
            <div role="group" aria-label="Status" className="flex items-center gap-1 p-[3px] rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
              {STATUS_TABS.map(([id, label]) => {
                const on = id === status;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setStatus(id)}
                    aria-pressed={on}
                    className="h-8 flex items-center gap-1.5 px-3 rounded-[7px] border text-[13px]"
                    style={{ borderColor: on ? 'var(--surf-line)' : 'transparent', background: on ? 'var(--surf-raised)' : 'transparent', color: on ? 'var(--ink-text)' : 'var(--ink-muted)' }}
                  >
                    {label}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{counts[id]}</span>
                  </button>
                );
              })}
            </div>
            {roleFilter && (
              <button
                type="button"
                onClick={() => setRoleFilter(null)}
                aria-label={`Clear role filter ${roleFilter}`}
                className="h-8 flex items-center gap-2 pl-3 pr-2.5 rounded-2xl border text-[13px]"
                style={{ borderColor: 'var(--accent)', color: 'var(--ink-text)' }}
              >
                Role: {roleFilter}<X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            )}
            <span className="ml-auto font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{shown.length} of {members.length}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse table-fixed">
              <thead>
                <tr className="h-10 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                  <th scope="col" className={`px-5 text-left font-medium ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>MEMBER</th>
                  <th scope="col" className={`w-[170px] px-3 text-left font-medium ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>ROLE</th>
                  <th scope="col" className={`w-[160px] px-3 text-left font-medium ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>STATUS</th>
                  <th scope="col" className={`w-[120px] px-3 text-left font-medium ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>ADDED</th>
                  <th scope="col" className="w-16 pr-5"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="h-32 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>Loading…</td></tr>
                )}
                {!loading && shown.length === 0 && (
                  <tr><td colSpan={5} className="h-32 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
                    {members.length ? 'No members match these filters.' : 'No members yet.'}
                  </td></tr>
                )}
                {!loading && shown.map(m => {
                  const pending = m.status !== 'active';
                  const isSelf = m.email?.toLowerCase() === myEmail;
                  const days = pending ? daysUntil(m.expires_at) : null;
                  const primary = pending ? m.email : (m.full_name || m.email);
                  const secondary = pending
                    ? (m.invited_by_email ? `Invited by ${m.invited_by_email}` : 'Invitation pending')
                    : (m.full_name ? m.email : null);
                  const roleEditable = !pending && !isSelf && can('users:update');
                  return (
                    <tr key={m.id} className="h-[60px] border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
                      <td className="px-5">
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className="w-[34px] h-[34px] box-border rounded-full border flex items-center justify-center text-xs font-semibold shrink-0"
                            style={{
                              background: pending ? 'transparent' : 'var(--surf-raised)',
                              borderStyle: pending ? 'dashed' : 'solid',
                              borderColor: 'var(--surf-line-strong)',
                              color: pending ? 'var(--ink-faint)' : 'var(--ink-text)',
                            }}
                          >
                            {initialsOf(pending ? null : m.full_name, m.email)}
                          </span>
                          <div className="flex flex-col gap-[3px] min-w-0">
                            <span className="flex items-center gap-2 text-sm font-medium min-w-0">
                              <span className="truncate">{primary}</span>
                              {isSelf && (
                                <span className="shrink-0 font-mono text-[10px] font-medium tracking-[0.08em] px-1.5 py-0.5 rounded-[5px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>YOU</span>
                              )}
                            </span>
                            {secondary && <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{secondary}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3">
                        {roleEditable ? (
                          <label className="relative inline-flex items-center max-w-[150px]">
                            <span className="sr-only">Change role for {primary}</span>
                            <select
                              value={m.role}
                              onChange={e => changeRole(m, e.target.value)}
                              className="h-8 max-w-[150px] pl-2.5 pr-7 rounded-lg border text-[13px] appearance-none cursor-pointer truncate"
                              style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)', color: 'var(--ink-text)' }}
                            >
                              {!roles.some(r => r.name === m.role) && <option value={m.role}>{m.role}</option>}
                              {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                            </select>
                            <svg aria-hidden="true" className="absolute right-2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-faint)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                          </label>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 h-8 text-[13px]" title={isSelf ? 'You can’t change your own role' : undefined}>
                            {roles.find(r => r.name === m.role)?.is_system && <KeyRound className="w-[13px] h-[13px]" style={{ color: 'var(--ink-muted)' }} strokeWidth={2} />}
                            {m.role}
                          </span>
                        )}
                      </td>
                      <td className="px-3">
                        <div className="flex flex-col gap-[3px]">
                          <span className="flex items-center gap-[7px] text-[13px]">
                            <span className="w-[7px] h-[7px] rounded-full" style={{ background: pending ? 'var(--sev-med-fg)' : 'var(--sev-good-fg)' }} />
                            {pending ? 'Invited' : 'Active'}
                          </span>
                          {pending && days != null && (
                            <span className="text-[11.5px]" style={{ color: days <= 0 ? 'var(--sev-crit-fg)' : 'var(--ink-faint)' }}>
                              {days <= 0 ? 'Expired' : `Expires in ${days} day${days === 1 ? '' : 's'}`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 font-mono text-xs" style={{ color: 'var(--ink-muted)' }}>{formatDate(m.created_at)}</td>
                      <td className="pr-5 text-right">
                        <RowMenu
                          member={m}
                          isSelf={isSelf}
                          can={can}
                          onResend={() => resend(m)}
                          onRemove={() => setConfirm({ type: 'remove', member: m })}
                          onRevoke={() => setConfirm({ type: 'revoke', member: m })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Roles (was the Role Manager tab) */}
        <section aria-label="Roles" className="box-border p-5 rounded-[14px] border flex flex-col gap-3.5" style={panelStyle}>
          <div className="flex items-center justify-between">
            <h2 className={`m-0 font-medium ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>ROLES</h2>
            <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Click a role to filter</span>
          </div>
          <div className="flex flex-col gap-2">
            {roles.map(r => {
              const on = roleFilter === r.name;
              const n = membersByRole[r.name] || 0;
              const permCount = (r.effective_permissions || []).length;
              const parent = r.parent_role_id ? roleById[r.parent_role_id] : null;
              return (
                <div key={r.id} className="flex items-stretch rounded-[10px] border" style={{ borderColor: on ? 'var(--accent)' : 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'var(--surf-bg)' }}>
                  <button
                    type="button"
                    onClick={() => setRoleFilter(on ? null : r.name)}
                    aria-pressed={on}
                    className="flex-grow min-w-0 flex flex-col items-start gap-[5px] px-3.5 py-3 text-left"
                  >
                    <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                      {r.name}
                      {r.is_system && (
                        <span className="font-mono text-[10px] font-medium tracking-[0.08em] px-1.5 py-0.5 rounded-[5px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>SYSTEM</span>
                      )}
                      {parent && <span className="text-[11.5px] font-normal" style={{ color: 'var(--ink-faint)' }}>inherits {parent.name}</span>}
                    </span>
                    {r.description && <span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-muted)' }}>{r.description}</span>}
                    <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                      {n} member{n === 1 ? '' : 's'} · {permCount} permission{permCount === 1 ? '' : 's'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDrawer({ role: r })}
                    aria-label={`${canEditRoles ? 'Edit' : 'View'} ${r.name} role`}
                    className="w-11 shrink-0 flex items-center justify-center border-l"
                    style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}
                  >
                    <Pencil className="w-[15px] h-[15px]" strokeWidth={1.8} />
                  </button>
                </div>
              );
            })}
            {!loading && roles.length === 0 && (
              <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>No roles yet.</span>
            )}
          </div>
          <p className="m-0 pt-3.5 border-t text-xs leading-relaxed" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>
            Permissions only add up: a role can inherit another role and grant more, never less. Everything else is denied by default.
          </p>
        </section>
      </div>

      <InviteDrawer
        open={drawer === 'invite'}
        onClose={() => setDrawer(null)}
        tenantId={tenantId}
        roles={roles}
        onInvited={load}
      />
      <RoleDrawer
        open={editingRole !== undefined}
        role={editingRole || null}
        roles={roles}
        permissions={allPerms}
        memberCount={editingRole ? (membersByRole[editingRole.name] || 0) : 0}
        canSave={editingRole ? canEditRoles : can('roles:create')}
        canDelete={can('roles:delete')}
        tenantId={tenantId}
        onClose={() => setDrawer(null)}
        onSaved={load}
      />
      <OrgDetailsDrawer
        open={drawer === 'org'}
        onClose={() => setDrawer(null)}
        tenantId={tenantId}
        org={org}
        myRole={myRole}
        canEdit={can('system:update') && !!org}
        onSaved={load}
      />

      <ConfirmModal
        isOpen={!!confirm}
        title={confirm?.type === 'remove' ? 'Remove member' : 'Revoke invitation'}
        message={confirm?.type === 'remove'
          ? `Remove ${confirm?.member.email} from ${orgName}? Their active session ends within 60 seconds.`
          : `Revoke the invitation for ${confirm?.member.email}?`}
        confirmLabel={confirm?.type === 'remove' ? 'Remove' : 'Revoke'}
        danger
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default OrgMembers;
